import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from '@klv/hooks/useTranslation';

import Icon from '@klv/atoms/icon';
import { UniversalMenuItemWithOptimizely } from '@klv/molecules/universal-menu-item/withOptimizely';
import { convertNamedColorToColorVariant, generateClassName } from '@klv/shared/utils/functions';
import { UniversalNavigationDrawer } from './children/drawer';
import { UniversalNavigationMobile } from './children/mobile';
import { AcademyCTAList } from './children/academy-cta-list';
import { gtmClick } from './utils';
import * as gridStyles from '@klv/utils/grid/styles/grid.module.css';
import * as styles from './styles/universalNavigation.module.css';

import type { IUniversalNavigation } from './types';

const handleResize = (mobileMenuRef: React.RefObject<HTMLDialogElement | null>) => {
  if (
    window.innerWidth >= 1024
    && mobileMenuRef?.current?.open
  ) {
    mobileMenuRef.current.close();
  }
};

export const UniversalNavigation = ({
  academyLoggedIn,
  academyProfileHref,
  children,
  className = '',
  color = 'lightSand',
  ctas = [],
  homeLink = '/',
  invertOnHover = true,
  menuItems = [],
  menuStyle = 'default',
  navigationStyle = 'default',
  platformActions = [],
  scrollDirection,
  siteActions = [],
  utilityItems = [],
  path = '',
  showSubnav = false,
  sites = [],
  translations = [],
  ...props
}: IUniversalNavigation) => {
  const { t } = useTranslation();

  const mobileMenuRef = useRef<HTMLDialogElement>(null);
  const [activeItemId, setActiveItemId] = useState<string | null>(null);

  const onResize = useCallback(() => handleResize(mobileMenuRef), [mobileMenuRef]);

  useEffect(() => {
    window.addEventListener('resize', onResize);

    return window.removeEventListener('resize', onResize);
  }, [onResize]);
  
  const isDefaultNav = navigationStyle !== 'minimal';
  
  const handleItemClick = (itemId: string) => {
    setActiveItemId(activeItemId === itemId ? null : itemId);
  };

  const handleMouseLeave = () => {
    setActiveItemId(null);
  };

  return (
    <div
      className={generateClassName([
        className,
        styles.universalNavigation,
        invertOnHover && styles.invertOnHover,
        styles[convertNamedColorToColorVariant(color)],
        scrollDirection === 'down' && styles.scrolled,
        showSubnav && styles.showSubnav,
      ])}
      {...props}
    >
      <div className={generateClassName([gridStyles.grid, styles.container])}>
        <a 
          title={t('organisms_navigation_logo_label')}
          href={homeLink}
          className={generateClassName([styles.logoContainer])}
        >
          <Icon
            className={generateClassName([styles.logo])}
            color='currentColor'
            iconName={"Logo"}
            size={3.6}
          />
          <Icon
            className={styles.flag}
            color='currentColor'
            iconName='BugMarkFilled'
            size={3}
          />
        </a>
        {isDefaultNav && (
          <>
            <nav 
              className={generateClassName([styles.nav])} 
              onMouseLeave={handleMouseLeave}
              onBlur={(e) => {
                // Check if the focus is moving outside the nav
                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                  setActiveItemId(null);
                }
              }}
            >
              <ul className={styles.list}>
                {menuItems.map(({ childItems, id, label, url: href }) => (
                  <li
                    className={generateClassName([
                      styles.listItem,
                      menuStyle === 'default' && childItems?.length && styles.hasChildren,
                    ])}
                    key={id}
                  >
                    <UniversalMenuItemWithOptimizely
                      aria-current={
                        (childItems?.some(({ url }) => url === path) && 'true')
                        || href === path
                        || undefined
                      }
                      as={(href === '#' || href === '') ? 'div' : undefined}
                      className={generateClassName([
                        styles.link,
                        childItems?.length && styles.hasChildren,
                        styles.openOnClick,
                        activeItemId === id && styles.active,
                      ])}
                      href={(href === '#' || href === '') ? undefined : href}
                      onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                        e.stopPropagation();
                        const target = e?.target as HTMLElement;
                        gtmClick({ childItem: label });
                        if ((href === '#' || href === '')) {
                          target.blur();
                        }
                        handleItemClick(id);
                      }}
                      onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                        if ((e.key === 'Enter' || e.key === ' ')) {
                          if (Array.isArray(childItems) && childItems.length > 0) {
                            e.stopPropagation();
                            e.preventDefault();
                            handleItemClick(id);
                          } else if (href && href !== '#' && href !== '') {
                            e.preventDefault();
                            window.location.href = href;
                          }
                        }
                      }}
                      onFocus={() => {
                        if (activeItemId && activeItemId !== id) {
                          setActiveItemId(null);
                        }
                      }}
                      tabIndex={(href === '#' || href === '') ? 0 : undefined}
                    >
                      <span>{label}</span>
                    </UniversalMenuItemWithOptimizely>
                    {menuStyle === 'default' && (
                      <UniversalNavigationDrawer
                        className={styles.drawer}
                        menuItems={childItems}
                        parentLabel={label}
                      />
                    )}
                  </li>
                ))}
              </ul>
            </nav>
            <AcademyCTAList
              academyLoggedIn={academyLoggedIn}
              ctas={ctas}
              profileHref={academyProfileHref}
            />
            <div className={styles.mobileMenuToggle}>
              <button aria-label="Toggle mobile menu" className={styles.toggleButton} onClick={() => mobileMenuRef?.current?.showModal()}>
                <svg width="24" height="18" viewBox="0 0 24 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="24" height="2" rx="1" fill="currentColor"/>
                  <rect y="8" width="24" height="2" rx="1" fill="currentColor"/>
                  <rect y="16" width="24" height="2" rx="1" fill="currentColor"/>
                </svg>
              </button>
            </div>
          </>
        )}
      </div>
      {isDefaultNav && (
        <>
          <UniversalNavigationMobile
            ctas={ctas}
            menuItems={menuItems}
            menuStyle={menuStyle}
            path={path}
            platformActions={platformActions}
            ref={mobileMenuRef}
            scrollDirection={scrollDirection}
            siteActions={siteActions}
            utilityItems={utilityItems}
            sites={sites}
            translations={translations}
          />
          {children}
        </>
      )}
    </div>
  );
};
